import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { APIProvider, Map, AdvancedMarker } from "@vis.gl/react-google-maps";
import {
  MapPinned, AlertTriangle, Radio, TrendingUp, TrendingDown,
  Clock, Wallet, Users, ShieldAlert, Star, FileWarning,
  ArrowLeft, Loader2, ExternalLink, MapPin,
  Map as MapIcon, Car, UsersRound, Siren, CreditCard, FileText,
  Layers, BarChart3, MessageCircle, X, Menu
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ExpressDriversPanel from "./ExpressDriversPanel";
import ExpressAdminPanel from "./ExpressAdminPanel";
import AdminExpressZonesTarifs from "./AdminExpressZonesTarifs";
import {
  ExpressBookingsPanel,
  ExpressPaymentsPanel,
  ExpressDocumentsPanel,
  ExpressStatsPanel,
  ExpressAlertsPanel,
} from "./AdminExpressPanels";

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Sidebar navigation — every item stays inside the Express shell using ?section=xxx
// so Express data stays completely isolated from the classic admin dashboard.
const SIDEBAR = [
  {
    group: "Opérations",
    items: [
      { id: "live",     label: "Suivi en direct",  Icon: MapIcon },
      { id: "bookings", label: "Courses",          Icon: Car },
      { id: "drivers",  label: "Chauffeurs",       Icon: UsersRound },
      { id: "alerts",   label: "Alertes",          Icon: Siren },
    ],
  },
  {
    group: "Gestion",
    items: [
      { id: "payments",  label: "Paiements",       Icon: CreditCard },
      { id: "documents", label: "Documents / KYC", Icon: FileText },
      { id: "zones",     label: "Zones & tarifs",  Icon: Layers },
    ],
  },
  {
    group: "Analyse",
    items: [
      { id: "stats",   label: "Statistiques",  Icon: BarChart3 },
      { id: "reviews", label: "Avis clients",  Icon: MessageCircle },
    ],
  },
];

const SECTION_TITLES = {
  live: "Suivi en direct",
  bookings: "Courses Express",
  drivers: "Chauffeurs Express",
  alerts: "Alertes",
  payments: "Paiements Express",
  documents: "Documents / KYC",
  zones: "Zones & tarifs",
  stats: "Statistiques",
  reviews: "Avis clients",
};

function fmtXAF(n) { return `${Number(n || 0).toLocaleString("fr-FR").replace(/,/g, " ")} F`; }
function fmtDuration(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m} min ${r.toString().padStart(2, "0")}`;
  }
  return `${s}s`;
}
function deltaChip(delta, positiveIsGood = true) {
  if (delta === null || delta === undefined || delta === 0) return null;
  const isUp = delta > 0;
  const good = positiveIsGood ? isUp : !isUp;
  const Icon = isUp ? TrendingUp : TrendingDown;
  return (
    <span className={`text-[11px] font-bold mt-1 flex items-center gap-1 ${good ? "text-[#3FAE7A]" : "text-[#C1453B]"}`}>
      <Icon className="w-3 h-3" /> {delta > 0 ? "+" : ""}{delta}
    </span>
  );
}

function StatusChip({ status }) {
  const map = {
    online:    { label: "Disponible", cls: "bg-emerald-50 text-emerald-700" },
    busy:      { label: "En course",  cls: "bg-amber-50 text-amber-700" },
    alert:     { label: "SOS / Suspendu", cls: "bg-red-50 text-red-700" },
    offline:   { label: "Hors ligne", cls: "bg-slate-100 text-slate-500" },
  };
  const v = map[status] || map.offline;
  return <span className={`text-[10.5px] font-bold px-2 py-0.5 rounded-lg ${v.cls}`}>{v.label}</span>;
}

function AlertIcon({ type }) {
  const map = {
    sos:    { Icon: ShieldAlert, bg: "bg-red-50", color: "text-red-600" },
    doc:    { Icon: FileWarning, bg: "bg-amber-50", color: "text-amber-600" },
    rating: { Icon: Star,        bg: "bg-blue-50", color: "text-blue-600" },
  };
  const v = map[type] || map.rating;
  const Icon = v.Icon;
  return (
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${v.bg}`}>
      <Icon className={`w-4 h-4 ${v.color}`} />
    </div>
  );
}

function DriverMarker({ driver, onClick }) {
  const bg = driver.status === "alert" ? "#C1453B" : driver.status === "busy" ? "#D98C1F" : "#3FAE7A";
  return (
    <AdvancedMarker position={{ lat: driver.lat, lng: driver.lng }} onClick={() => onClick(driver)}>
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] shadow-[0_0_0_5px_rgba(63,174,122,0.2)] animate-pulse"
        style={{ background: bg }}
        title={driver.name}
      >
        🏍️
      </div>
    </AdvancedMarker>
  );
}

function LiveMap({ drivers, zones, mapCenter, selectedDriver, onSelect }) {
  return (
    <div className="relative h-[360px] rounded-xl overflow-hidden bg-[#dfe4d6]">
      <Map
        defaultCenter={mapCenter || { lat: 4.0511, lng: 9.7679 }}
        defaultZoom={12}
        gestureHandling="greedy"
        disableDefaultUI
        mapId="trigo-express-admin-map"
        style={{ width: "100%", height: "100%" }}
      >
        {drivers.filter((d) => d.lat != null && d.lng != null).map((d) => (
          <DriverMarker key={d.id} driver={d} onClick={onSelect} />
        ))}
      </Map>
      {/* Zone labels overlay */}
      <div className="absolute inset-0 pointer-events-none">
        {(zones || []).map((z) => (
          <span key={z.name} className="absolute text-[10.5px] font-bold text-[#5B6B7A] bg-white/70 px-1.5 py-0.5 rounded" style={{
            left: `${((z.lng - 9.62) / 0.20) * 100}%`,
            top: `${((4.10 - z.lat) / 0.10) * 100}%`,
            transform: "translate(-50%, -50%)",
          }}>{z.name}</span>
        ))}
      </div>
      {selectedDriver && (
        <div className="absolute bottom-3 left-3 right-3 bg-white/95 backdrop-blur border border-[#E4E0D8] rounded-xl px-3 py-2 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#F4F2ED] flex items-center justify-center font-bold text-[#0F2B46]">
            {(selectedDriver.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm text-[#0F2B46]">{selectedDriver.name}</div>
            <div className="text-[11px] text-[#5B6B7A]">Zone {selectedDriver.zone} · {selectedDriver.rides_today} courses aujourd&apos;hui</div>
          </div>
          <StatusChip status={selectedDriver.status} />
          <button onClick={() => onSelect(null)} className="text-[#5B6B7A] hover:text-[#0F2B46] text-lg leading-none">×</button>
        </div>
      )}
    </div>
  );
}

export default function AdminExpressLiveDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const sectionFromUrl = (() => {
    const p = new URLSearchParams(location.search);
    return p.get("section") || "live";
  })();
  const [section, setSection] = useState(sectionFromUrl);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const timerRef = useRef(null);

  // Sync section state with URL changes (back/forward navigation)
  useEffect(() => {
    setSection(sectionFromUrl);
  }, [sectionFromUrl]);

  const goToSection = (id) => {
    const params = new URLSearchParams(location.search);
    if (id === "live") {
      params.delete("section");
    } else {
      params.set("section", id);
    }
    const qs = params.toString();
    navigate(`/admin/express-live${qs ? `?${qs}` : ""}`);
    setSection(id);
    setSidebarOpen(false);
  };

  const load = async (silent = false) => {
    if (!silent) setLoading(true); else setRefreshing(true);
    try {
      const { data: fresh } = await api.get("/admin/express/live");
      setData(fresh);
    } catch (e) {
      if (!silent) toast.error("Impossible de charger le dashboard");
    } finally {
      if (!silent) setLoading(false); else setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => load(true), 30000); // refresh every 30 s
    return () => timerRef.current && clearInterval(timerRef.current);
  }, []);

  if (loading || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F2ED]">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F2B46]" />
      </div>
    );
  }

  const { kpis, alerts } = data;
  const alertsCount = alerts.length;
  const bookingsCount = kpis.rides_in_progress;

  return (
    <div className="min-h-screen bg-[#F4F2ED]">
      {/* Mobile top bar (sidebar toggle) */}
      <div className="lg:hidden sticky top-0 z-30 bg-[#0F2B46] text-white flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-2 font-extrabold text-sm" style={{ fontFamily: "Sora" }}>
          <span className="w-2.5 h-2.5 rounded-full bg-[#F2A93B]" /> Trigo Express
        </div>
        <button onClick={() => setSidebarOpen(true)} className="p-1.5 rounded hover:bg-white/10" aria-label="Ouvrir le menu" data-testid="admin-express-sidebar-open">
          <Menu className="w-5 h-5" />
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <ExpressSidebar
          activeId={section}
          goToSection={goToSection}
          navigate={navigate}
          alertsCount={alertsCount}
          bookingsCount={bookingsCount}
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        {/* Main */}
        <div className="flex-1 min-w-0 p-4 sm:p-6">
          <div className="max-w-[1400px]">
            <div className="hidden lg:flex items-center gap-2 mb-4">
              <button onClick={() => navigate("/admin")} className="flex items-center gap-1 text-[#5B6B7A] text-sm hover:text-[#0F2B46]" data-testid="admin-express-live-back">
                <ArrowLeft className="w-4 h-4" /> Retour à l&apos;administration
              </button>
            </div>

            {/* Section topbar */}
            <div className="flex flex-wrap items-start justify-between gap-3 mb-5" data-testid="admin-express-live-topbar">
              <div>
                <h1 className="text-xl sm:text-2xl font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>
                  Trigo Express — {SECTION_TITLES[section] || SECTION_TITLES.live}
                </h1>
                <p className="text-xs sm:text-sm text-[#5B6B7A] mt-1 capitalize">
                  {new Date().toLocaleString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                </p>
              </div>
              <div className="flex items-center gap-2 bg-white border border-[#E4E0D8] px-3 py-1.5 rounded-full text-sm font-bold text-[#3FAE7A]">
                <Radio className="w-4 h-4 animate-pulse" />
                <span data-testid="admin-express-online-count">{kpis.drivers_online}</span> chauffeurs en ligne
                {refreshing && <Loader2 className="w-3 h-3 animate-spin text-[#5B6B7A] ml-1" />}
              </div>
            </div>

            {/* Section content */}
            {section === "live" && <LiveSection data={data} selectedDriver={selectedDriver} setSelectedDriver={setSelectedDriver} navigate={navigate} />}
            {section === "bookings" && <ExpressBookingsPanel />}
            {section === "drivers" && <ExpressDriversPanel />}
            {section === "alerts" && <ExpressAlertsPanel liveData={data} />}
            {section === "payments" && <ExpressPaymentsPanel />}
            {section === "documents" && <ExpressDocumentsPanel />}
            {section === "zones" && (
              <div className="space-y-4">
                <AdminExpressZonesTarifs />
                <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Layers className="w-5 h-5 text-[#F2A93B]" />
                    <h2 className="text-lg font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Types de véhicules & réglages globaux</h2>
                  </div>
                  <ExpressAdminPanel />
                </div>
              </div>
            )}
            {section === "stats" && <ExpressStatsPanel />}
            {section === "reviews" && <ExpressStatsPanel />}
          </div>
        </div>
      </div>
    </div>
  );
}

function LiveSection({ data, selectedDriver, setSelectedDriver, navigate }) {
  const { kpis, drivers, alerts, zones, map_center } = data;
  const waitDelta = kpis.avg_wait_seconds - (kpis.avg_wait_seconds_yday || 0);
  const revenuePct = kpis.revenue_yday_xaf ? Math.round(((kpis.revenue_today_xaf - kpis.revenue_yday_xaf) / kpis.revenue_yday_xaf) * 100) : 0;
  const cancelDelta = Math.round((kpis.cancellation_rate_pct - kpis.cancellation_rate_pct_yday) * 10) / 10;
  const ridesDelta = kpis.rides_completed_today - (kpis.rides_completed_yday || 0);

  const activeDrivers = [...drivers].sort((a, b) => {
    const order = { alert: 0, busy: 1, online: 2, offline: 3 };
    return (order[a.status] ?? 9) - (order[b.status] ?? 9);
  }).slice(0, 6);

  return (
    <>
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="admin-express-kpi-row">
        <KPI icon={Users} label="Courses en cours" value={kpis.rides_in_progress} delta={ridesDelta} deltaLabel="vs hier" positiveIsGood testId="kpi-rides-in-progress" />
        <KPI icon={Clock} label="Temps d'attente moyen" value={fmtDuration(kpis.avg_wait_seconds)} delta={waitDelta} deltaLabel="s vs hier" positiveIsGood={false} testId="kpi-avg-wait" />
        <KPI icon={Wallet} label="Revenu du jour" value={fmtXAF(kpis.revenue_today_xaf)} delta={revenuePct} deltaLabel="% vs hier" positiveIsGood testId="kpi-revenue" />
        <KPI icon={AlertTriangle} label="Taux d'annulation" value={`${kpis.cancellation_rate_pct.toFixed(1)}%`} delta={cancelDelta} deltaLabel="pt vs hier" positiveIsGood={false} testId="kpi-cancel-rate" />
      </div>

      {/* Map + Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-4 mb-4">
        <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Position des chauffeurs</h2>
            <span className="text-[11px] text-[#5B6B7A]">
              <MapPin className="w-3 h-3 inline mr-0.5" />
              {drivers.filter((d) => d.lat != null).length} sur carte
            </span>
          </div>
          {MAPS_KEY ? (
            <APIProvider apiKey={MAPS_KEY} libraries={["marker"]}>
              <LiveMap drivers={drivers} zones={zones} mapCenter={map_center} selectedDriver={selectedDriver} onSelect={setSelectedDriver} />
            </APIProvider>
          ) : (
            <div className="h-[360px] rounded-xl bg-slate-100 flex items-center justify-center text-sm text-[#5B6B7A]">Clé Google Maps manquante</div>
          )}
          <div className="flex gap-4 mt-3 text-[11px] text-[#5B6B7A]" data-testid="admin-express-map-legend">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#3FAE7A]" /> Disponible</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#D98C1F]" /> En course</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-[#C1453B]" /> Alerte / SOS</span>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-alerts">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2B46] mb-3" style={{ fontFamily: "Sora" }}>Alertes prioritaires</h2>
          {alerts.length === 0 ? (
            <div className="py-8 text-center text-sm text-[#5B6B7A]">
              Aucune alerte active 🎉
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((a, i) => (
                <div key={i} className="flex gap-2.5 pb-3 border-b border-dashed border-[#E4E0D8] last:border-b-0" data-testid={`admin-express-alert-${i}`}>
                  <AlertIcon type={a.type} />
                  <div className="min-w-0">
                    <div className="text-[12.5px] font-semibold text-[#142433]">{a.title}</div>
                    <div className="text-[11px] text-[#5B6B7A]">{a.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Bottom: Active drivers + Demand vs supply */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-active-drivers">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2B46]" style={{ fontFamily: "Sora" }}>Chauffeurs actifs</h2>
            <button onClick={() => navigate("/admin/express-live?section=drivers")} className="text-[11px] text-[#D98C1F] font-semibold hover:underline flex items-center gap-1">Voir tous <ExternalLink className="w-3 h-3" /></button>
          </div>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Chauffeur</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Zone</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Statut</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Courses</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Note</th>
              </tr>
            </thead>
            <tbody>
              {activeDrivers.map((d) => (
                <tr key={d.id} className="border-b border-[#F0EDE6]" data-testid={`admin-express-driver-row-${d.id}`}>
                  <td className="py-2">
                    <div className="flex items-center gap-2 font-semibold">
                      <span className="w-6 h-6 rounded-lg bg-[#F4F2ED] flex items-center justify-center text-[11px] font-extrabold text-[#0F2B46]">{(d.name || "?").slice(0, 2).toUpperCase()}</span>
                      {d.name}
                    </div>
                  </td>
                  <td className="text-[#5B6B7A]">{d.zone}</td>
                  <td><StatusChip status={d.status} /></td>
                  <td>{d.rides_today}</td>
                  <td>{d.rating ? d.rating.toFixed(1) : "—"}</td>
                </tr>
              ))}
              {activeDrivers.length === 0 && (
                <tr><td colSpan={5} className="py-6 text-center text-[#5B6B7A]">Aucun chauffeur Express intégré</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid="admin-express-demand-supply">
          <h2 className="text-sm font-bold uppercase tracking-wider text-[#0F2B46] mb-3" style={{ fontFamily: "Sora" }}>Répartition demande / offre</h2>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Zone</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Demandes/h</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Dispo.</th>
                <th className="text-left py-1 text-[11px] uppercase font-semibold text-[#5B6B7A] border-b border-[#E4E0D8]">Écart</th>
              </tr>
            </thead>
            <tbody>
              {zones.map((z) => {
                const gap = z.gap;
                const color = gap < -4 ? "text-[#C1453B]" : gap < 0 ? "text-[#D98C1F]" : "text-[#3FAE7A]";
                return (
                  <tr key={z.name} className="border-b border-[#F0EDE6]" data-testid={`admin-express-zone-row-${z.name}`}>
                    <td className="py-2 font-semibold text-[#142433]">{z.name}</td>
                    <td>{z.demand_per_hour}</td>
                    <td>{z.drivers_available}</td>
                    <td className={`font-bold ${color}`}>{gap > 0 ? "+" : ""}{gap}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-[10.5px] text-[#5B6B7A] mt-2 leading-relaxed">
            <MapPinned className="w-3 h-3 inline mr-1" /> Zones à écart négatif = pénurie de chauffeurs → envisager un <b>bonus zone chaude</b> ou du recrutement local.
          </p>
        </div>
      </div>
    </>
  );
}

function ExpressSidebar({ activeId, goToSection, navigate, alertsCount, bookingsCount, open, onClose }) {
  const badges = { alerts: alertsCount || 0, bookings: bookingsCount || 0 };
  return (
    <>
      {/* Backdrop mobile */}
      {open && <div className="lg:hidden fixed inset-0 bg-black/40 z-40" onClick={onClose} />}
      <aside
        className={`bg-[#0F2B46] text-white w-[220px] flex-shrink-0 p-5 flex flex-col ${
          open ? "fixed inset-y-0 left-0 z-50" : "hidden"
        } lg:sticky lg:top-0 lg:flex lg:h-screen`}
        data-testid="admin-express-sidebar"
      >
        <div className="flex items-center justify-between mb-6">
          <div className="font-extrabold text-[17px] flex items-center gap-2" style={{ fontFamily: "Sora" }}>
            <span className="w-2.5 h-2.5 rounded-full bg-[#F2A93B]" /> Trigo Express
          </div>
          <button onClick={onClose} className="lg:hidden text-white/70 hover:text-white" aria-label="Fermer le menu">
            <X className="w-5 h-5" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto pr-1">
          {SIDEBAR.map((group) => (
            <div key={group.group} className="mb-5">
              <div className="text-[10px] uppercase tracking-widest text-[#7E96AC] mb-2 px-2">{group.group}</div>
              {group.items.map((it) => {
                const active = it.id === activeId;
                const badge = it.id === "alerts" ? badges.alerts : it.id === "bookings" ? badges.bookings : 0;
                const Icon = it.Icon;
                return (
                  <button
                    key={it.id}
                    type="button"
                    onClick={() => goToSection(it.id)}
                    className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-semibold mb-0.5 transition-colors text-left ${
                      active ? "bg-white/10 text-white" : "text-[#CFE0EF] hover:bg-white/5 hover:text-white"
                    }`}
                    data-testid={`admin-express-sidebar-${it.id}`}
                  >
                    <Icon className="w-4 h-4" />
                    <span className="flex-1">{it.label}</span>
                    {badge > 0 && (
                      <span className="bg-[#C1453B] text-white text-[10px] font-extrabold px-1.5 py-0.5 rounded-md">{badge}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </nav>
        <button
          onClick={() => navigate("/admin")}
          className="mt-3 text-[11px] text-[#CFE0EF] hover:text-white flex items-center gap-1"
        >
          <ArrowLeft className="w-3 h-3" /> Retour admin classique
        </button>
      </aside>
    </>
  );
}

function KPI({ icon: Icon, label, value, delta, deltaLabel, positiveIsGood, testId }) {
  return (
    <div className="bg-white border border-[#E4E0D8] rounded-2xl p-4" data-testid={testId}>
      <div className="flex items-center gap-1.5 text-[11.5px] text-[#5B6B7A] mb-1"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="text-xl font-extrabold text-[#0F2B46]" style={{ fontFamily: "Sora" }}>{value}</div>
      {delta !== null && delta !== undefined && delta !== 0 ? (
        <span className={`text-[11px] font-bold mt-1 flex items-center gap-1 ${(positiveIsGood ? delta > 0 : delta < 0) ? "text-[#3FAE7A]" : "text-[#C1453B]"}`}>
          {(positiveIsGood ? delta > 0 : delta < 0) ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          {delta > 0 ? "+" : ""}{typeof delta === "number" ? delta : ""} {deltaLabel}
        </span>
      ) : (
        <span className="text-[11px] text-[#94A3B8] mt-1 block">—</span>
      )}
    </div>
  );
}
